/**
 * Bridge API Client
 * Centralized client for the multi-chain CSPR Bridge API
 */

import { ZERO_ADDRESS } from 'config/chains';
import type {
  Chain,
  MultiChainToken,
  ApiTransaction,
  TransactionsApiResponse,
  TransactionQueryParams,
  ChainExplorers,
  ExplorersApiResponse,
  HealthCheckResponse,
  BridgeApiError,
} from 'types';

/**
 * Get the API base URL from global config
 * Returns null if no API URL is configured
 */
const getApiBaseUrl = (): string | null => {
  const url = config.api_url;
  // Only return URL if it's configured and not empty
  if (url && url.trim() !== '') {
    return url;
  }

  return null;
};

/**
 * Check if the Bridge API is configured
 */
export function isApiConfigured(): boolean {
  return getApiBaseUrl() !== null;
}

/**
 * Generic fetch wrapper with error handling
 * Throws if API is not configured
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const baseUrl = getApiBaseUrl();

  if (!baseUrl) {
    throw new Error('Bridge API is not configured');
  }

  const url = `${baseUrl}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    let errorData: BridgeApiError;
    try {
      errorData = await response.json();
    } catch {
      errorData = {
        error: 'API Error',
        message: `Request failed with status ${response.status}`,
      };
    }
    throw Object.assign(new Error(errorData.message || `API request failed: ${response.status}`), { status: response.status });
  }

  return response.json();
}

export interface ApiBuildInfo { component: 'api'; version: string; sourceSha: string | null }
export async function getApiVersion(signal?: AbortSignal): Promise<ApiBuildInfo> {
  const response = await apiFetch<{success: boolean; data: ApiBuildInfo}>('/api/v1/version', {signal, cache: 'no-store'});
  const data = response?.data;
  if (response?.success !== true || data?.component !== 'api' || typeof data.version !== 'string' || !/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/.test(data.version) ||
      (data.sourceSha !== null && (typeof data.sourceSha !== 'string' || !/^[a-f0-9]{40}$/.test(data.sourceSha)))) throw new Error('Invalid API version response');
  return data;
}

/**
 * Raw API response types (what the API actually returns)
 */
interface RawChainResponse {
  chainId: number;
  name: string;
  shortName: string;
  explorerUrl: string;
  lockerAddress: string | null;
  nativeCurrency: string;
  confirmations: number;
  blockTime: string;
  isEnabled: boolean;
  isPrimary: boolean;
}

interface RawTokenResponse {
  id: number;
  casperHash: string;
  symbol: string;
  name: string;
  decimalsEth: number;
  decimalsCasper: number;
  originChain: string;
  wrappedAddress: string | null;
  isActive: boolean;
  addresses: Record<string, string>;
  /**
   * Available liquidity on EVM chains (formatted amounts like "5000.00")
   * For EVM-origin tokens: Per-chain amounts locked in MultiSigLocker contracts
   * For Casper-origin tokens: Empty object {} (they don't have EVM liquidity pools)
   */
  evmLiquidity: Record<string, string> | null;
  /**
   * Available liquidity on Casper (formatted amount like "10000.00")
   * For Casper-origin tokens: Amount locked in TokenFactory (global pool)
   * For EVM-origin tokens: null (they don't have Casper liquidity pool)
   */
  casperLiquidity: string | null;
}

interface RawApiResponse<T> {
  success: boolean;
  data: T[];
}

/**
 * Raw transaction response from API
 */
interface RawTransactionResponse {
  id: string | number;
  txType: string;
  direction: string; // 'eth2casper' | 'casper2eth'
  evmChainId: number | null;
  evmChainName: string | null;
  sourceChain: string;
  sourceTxHash: string | null;
  sourceEventIndex?: number | null;
  sourceTxIndex?: number | null;
  /** Exposure-control transfers only: lowercase hex without 0x. */
  transferId?: string | null;
  sourceBlock: number | null;
  destinationChain: string;
  destinationTxHash: string | null;
  destinationBlock: number | null;
  amount: string;
  sender: string;
  recipient: string;
  senderPublicKey?: string | null;
  recipientPublicKey?: string | null;
  status: string; // 'detected', 'pending', 'completed', 'failed', etc.
  signaturesCollected: number;
  signaturesRequired: number;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  token: {
    symbol: string;
    name: string;
    decimalsEth: number;
  };
  explorerLinks: {
    source: { url: string; explorerName: string } | null;
    destination: { url: string; explorerName: string } | null;
  };
}

interface RawTransactionsApiResponse {
  success: boolean;
  data: RawTransactionResponse[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Transform raw chain response to our Chain type
 */
function transformChain(raw: RawChainResponse): Chain {
  // Normalize chain name to lowercase to match token address keys
  const normalizedName = raw.shortName.toLowerCase().replace(/\s+/g, '-');

  return {
    chainId: raw.chainId,
    name: normalizedName, // Use normalized shortName as the key name for addresses
    shortName: raw.name, // Use full name as display name
    explorerUrl: raw.explorerUrl,
    // The API is the source of truth for lockers. Never back-fill from the
    // static LOCKER_ADDRESSES map here: that map is the make-dev fleet,
    // so on any other environment it would silently route a user's funds to a locker
    // this environment's relayers do not watch. A chain the API returns
    // without a locker is not bridgeable; ZERO_ADDRESS makes
    // bridgeService.getLockerAddress() refuse it loudly.
    lockerAddress: raw.lockerAddress ?? ZERO_ADDRESS,
    confirmations: raw.confirmations,
    blockTime: parseFloat(raw.blockTime),
    isEnabled: raw.isEnabled,
  };
}

/**
 * Transform raw token response to our MultiChainToken type
 */
function transformToken(raw: RawTokenResponse): MultiChainToken {
  // Strip 'hash-' or '0x' prefix from casperHash for consistent comparison
  let casperHash = raw.casperHash || '';
  if (casperHash.startsWith('hash-')) {
    casperHash = casperHash.slice(5);
  }
  if (casperHash.startsWith('0x')) {
    casperHash = casperHash.slice(2);
  }

  // Map API origin chain to our type (API uses 'ethereum'/'casper', we use 'evm'/'casper')
  const originChain = raw.originChain === 'casper' ? 'casper' : 'evm';

  return {
    symbol: raw.symbol,
    name: raw.name,
    decimals: raw.decimalsEth, // Use EVM decimals for bridge operations
    casperHash,
    addresses: raw.addresses,
    originChain,
    // Pass through liquidity fields as-is from API
    // evmLiquidity: per-chain mapping for EVM-origin tokens (null for Casper-origin)
    // casperLiquidity: global pool for Casper-origin tokens (null for EVM-origin)
    evmLiquidity: raw.evmLiquidity,
    casperLiquidity: raw.casperLiquidity,
  };
}

/**
 * Transform raw transaction response to our ApiTransaction type
 */
function transformTransaction(raw: RawTransactionResponse): ApiTransaction {
  // Map API direction to our format
  const direction = raw.direction === 'eth2casper' ? 'eth_to_casper' : 'casper_to_eth';

  // Map API status to our status format
  let status: 'pending' | 'completed' | 'failed' = 'pending';
  if (raw.status === 'completed' || raw.status === 'minted' || raw.status === 'unlocked') {
    status = 'completed';
  } else if (raw.status === 'failed' || raw.status === 'error') {
    status = 'failed';
  }

  // Build explorer links
  const explorerLinks = {
    source: raw.explorerLinks?.source?.url,
    destination: raw.explorerLinks?.destination?.url,
  };

  // Build steps based on transaction type and status
  const steps = buildTransactionSteps(raw);

  return {
    id: String(raw.id),
    sourceTxHash: raw.sourceTxHash,
    sourceChain: raw.sourceChain,
    evmChainId: raw.evmChainId,
    sourceEventIndex: raw.sourceEventIndex,
    sourceTxIndex: raw.sourceTxIndex,
    transferId: typeof raw.transferId === 'string' && /^[0-9a-f]{64}$/.test(raw.transferId) ? `0x${raw.transferId}` : null,
    txType: raw.txType,
    direction,
    chainName: raw.evmChainName || raw.sourceChain,
    tokenSymbol: raw.token?.symbol || 'UNKNOWN',
    amount: raw.amount,
    sourceAddress: raw.sender,
    destinationAddress: raw.recipient,
    senderPublicKey: raw.senderPublicKey,
    recipientPublicKey: raw.recipientPublicKey,
    status,
    rawStatus: raw.status,
    signaturesCollected: raw.signaturesCollected ?? 0,
    signaturesRequired: raw.signaturesRequired ?? 0,
    steps,
    explorerLinks,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
}

/**
 * Build transaction steps based on transaction type and status.
 * Finality/attestation split uses the indexer's status enum (task #2577):
 * 'confirming' = source still inside the finality window; 'finalized' (and
 * 'attesting'/'submitted') = source finality proven. Core PR102 produces
 * 'submitted' from destination EVM event observations; canonical scanning
 * alone completes them. Casper destinations may complete directly.
 */
function buildTransactionSteps(raw: RawTransactionResponse): ApiTransaction['steps'] {
  const isEthToCasper = raw.direction === 'eth2casper';
  const isPastFinality = ['finalized', 'attesting', 'submitted'].includes(raw.status);

  if (isEthToCasper) {
    // ETH -> Casper: lock, finality, attestation, mint
    const hasLock = !!raw.sourceTxHash;
    const hasMint = !!raw.destinationTxHash;
    const isComplete = raw.status === 'completed' || raw.status === 'minted';

    return [
      {
        step: 'lock',
        status: hasLock ? 'completed' : 'active',
        txHash: raw.sourceTxHash || undefined,
      },
      {
        step: 'finality',
        status: isComplete || hasMint || isPastFinality ? 'completed' : hasLock ? 'active' : 'pending',
      },
      {
        step: 'attestation',
        status: isComplete || hasMint ? 'completed' : isPastFinality ? 'active' : 'pending',
      },
      {
        step: ['burn_wrapped', 'unlock_native'].includes(raw.txType) ? 'unlock_native' : 'mint',
        status: isComplete ? 'completed' : hasMint ? 'active' : 'pending',
        txHash: raw.destinationTxHash || undefined,
      },
    ];
  } else {
    // Casper -> ETH: burn, attestation, unlock. Casper finality is instant,
    // so burn rows are born 'finalized' — attestation is active immediately.
    const hasBurn = !!raw.sourceTxHash;
    const hasUnlock = !!raw.destinationTxHash;
    const isComplete = raw.status === 'completed' || raw.status === 'unlocked';

    return [
      {
        step: 'burn',
        status: hasBurn ? 'completed' : 'active',
        txHash: raw.sourceTxHash || undefined,
      },
      {
        step: 'attestation',
        status: isComplete || hasUnlock ? 'completed' : hasBurn ? 'active' : 'pending',
      },
      {
        step: ['lock_native', 'mint_wrapped'].includes(raw.txType) ? 'mint_wrapped' : 'unlock',
        status: isComplete ? 'completed' : hasUnlock ? 'active' : 'pending',
        txHash: raw.destinationTxHash || undefined,
      },
    ];
  }
}

/**
 * Fetch all supported EVM chains
 */
export async function fetchChains(): Promise<Chain[]> {
  const response = await apiFetch<RawApiResponse<RawChainResponse>>('/api/v1/chains');

  return response.data.map(transformChain);
}

/**
 * Fetch all supported tokens with their multi-chain addresses
 */
export async function fetchTokens(): Promise<MultiChainToken[]> {
  const response = await apiFetch<RawApiResponse<RawTokenResponse>>('/api/v1/tokens');

  return response.data.map(transformToken);
}

/**
 * Fetch tokens filtered by a specific chain
 * Only returns tokens that have an address on the specified chain
 */
export async function fetchTokensByChain(chainName: string): Promise<MultiChainToken[]> {
  const response = await apiFetch<RawApiResponse<RawTokenResponse>>(`/api/v1/tokens/chain/${chainName}`);

  return response.data.map(transformToken);
}

/**
 * Fetch transaction history with optional filters
 */
export async function fetchTransactions(
  params: TransactionQueryParams = {}
): Promise<TransactionsApiResponse> {
  const searchParams = new URLSearchParams();

  if (params.page !== undefined) {
    searchParams.set('page', String(params.page));
  }
  if (params.pageSize !== undefined) {
    // API paginates with `limit`, not `pageSize`
    searchParams.set('limit', String(params.pageSize));
  }
  // NOTE deliberately omitted params:
  // - status: the API's status vocabulary is the fine-grained pipeline set
  //   (detected/confirming/finalized/attesting/submitted/completed/failed);
  //   our coarse 'pending' has no API equivalent and would 400. Status
  //   filtering stays client-side (see useApiTransactions.filterByStatus).
  // - chain: the list route has no chain filter; filtering is client-side.

  const queryString = searchParams.toString();

  // The list route's `address` query param only accepts EVM (0x…) addresses;
  // Casper public keys would 400. The dedicated /address/:address route
  // matches sender OR recipient for both address formats.
  const endpoint = params.address
    ? `/api/v1/transactions/address/${encodeURIComponent(params.address)}${queryString ? `?${queryString}` : ''}`
    : queryString
      ? `/api/v1/transactions?${queryString}`
      : '/api/v1/transactions';

  const rawResponse = await apiFetch<RawTransactionsApiResponse>(endpoint);

  // Transform raw API response to our internal types
  // Guard against malformed API responses (e.g. when testnet is degraded)
  const rawData = Array.isArray(rawResponse?.data) ? rawResponse.data : [];

  return {
    data: rawData.map(transformTransaction),
    pagination: rawResponse.pagination
      ? {
          total: rawResponse.pagination.total,
          page: rawResponse.pagination.page,
          pageSize: rawResponse.pagination.limit,
          hasMore: rawResponse.pagination.page < rawResponse.pagination.totalPages,
        }
      : undefined,
  };
}

// With `allowMissing=true` the API answers a source it has not indexed yet with 200 and `data: null`, so
// polling for a new beam leaves no red 404 in the console. An API without the parameter rejects it with
// 400 (strict query); from then on this page asks without it and a missing source is a 404 again.
let allowMissingUnsupported = false;

/**
 * Fetch by submitted source hash, independently of address-history pagination.
 * Resolves to null while the API has not indexed the source; an API without `allowMissing` rejects
 * with status 404 for that instead.
 */
export async function fetchTransactionBySourceHash(
  sourceTxHash: string,
  signal?: AbortSignal,
  event?: { sourceChainId: number; sourceEventIndex: number }
): Promise<ApiTransaction | null> {
  const selector = event ? `?sourceChainId=${event.sourceChainId}&sourceEventIndex=${event.sourceEventIndex}` : '';
  const lookup = (allowMissing: boolean) => apiFetch<{ data: RawTransactionResponse | null }>(
    `/api/v1/transactions/hash/${encodeURIComponent(sourceTxHash)}${selector}` +
      (allowMissing ? `${selector ? '&' : '?'}allowMissing=true` : ''),
    { signal }
  );
  let response: { data: RawTransactionResponse | null };
  if (allowMissingUnsupported) {
    response = await lookup(false);
  } else {
    try {
      response = await lookup(true);
    } catch (err) {
      if ((err as { status?: number }).status !== 400) throw err;
      allowMissingUnsupported = true;
      response = await lookup(false);
    }
  }

  return response.data ? transformTransaction(response.data) : null;
}

/**
 * Fetch a single transaction by ID
 */
export async function fetchTransaction(transactionId: string, signal?: AbortSignal): Promise<ApiTransaction> {
  const response = await apiFetch<{ data: RawTransactionResponse }>(`/api/v1/transactions/${transactionId}`, { signal });

  // Same transform as the list endpoint — the raw API shape (eth2casper,
  // sender/recipient, no steps[]) must be normalized before the UI sees it.
  return transformTransaction(response.data);
}

/**
 * Fetch explorer URLs for all chains
 */
export async function fetchExplorers(): Promise<ChainExplorers> {
  const response = await apiFetch<ExplorersApiResponse>('/api/v1/chains/explorers/all');

  return response.data;
}

/**
 * Check API health status
 */
export async function checkHealth(): Promise<HealthCheckResponse> {
  return apiFetch<HealthCheckResponse>('/api/v1/stats/health');
}

/** Live bridge fees read from chain state by the API (never hardcoded) */
export interface BridgeFees {
  casper: { feeMotes: string; feeCspr: number } | null;
  evm: Record<string, { feeWei: string; nativeCurrency: string } | null>;
}

/**
 * Fetch live protocol bridge fees.
 * casper.feeMotes = TokenFactory FeeConfig.fee (burn / lock_native payment)
 * evm[chain].feeWei = locker bridgeFee() (lock / burnWrapped msg.value)
 */
export async function fetchBridgeFees(): Promise<BridgeFees> {
  const response = await apiFetch<{ data: BridgeFees }>('/api/v1/stats/bridge-fee');

  return response.data;
}

/**
 * Helper: Get explorer URL for a transaction
 */
export function getChainExplorerTxUrl(chain: Chain, txHash: string): string {
  return `${chain.explorerUrl}/tx/${txHash}`;
}

/**
 * Helper: Get explorer URL for an address
 */
export function getChainExplorerAddressUrl(chain: Chain, address: string): string {
  return `${chain.explorerUrl}/address/${address}`;
}

/**
 * Helper: Filter tokens available on a specific chain (case-insensitive)
 */
export function filterTokensByChain(tokens: MultiChainToken[], chainName: string): MultiChainToken[] {
  const normalizedChainName = chainName.toLowerCase();

  return tokens.filter(token => {
    // Direct lookup
    if (token.addresses[chainName] !== undefined) return true;
    // Case-insensitive lookup
    return Object.keys(token.addresses).some(key => key.toLowerCase() === normalizedChainName);
  });
}

/**
 * Helper: Get token address for a specific chain (case-insensitive)
 */
export function getTokenAddressForChain(token: MultiChainToken, chainName: string): string | undefined {
  // Direct lookup
  if (token.addresses[chainName]) {
    return token.addresses[chainName];
  }
  // Case-insensitive lookup
  const normalizedChainName = chainName.toLowerCase();
  const matchingKey = Object.keys(token.addresses).find(
    key => key.toLowerCase() === normalizedChainName
  );

  return matchingKey ? token.addresses[matchingKey] : undefined;
}

/**
 * Helper: Find a token by symbol
 */
export function findTokenBySymbol(tokens: MultiChainToken[], symbol: string): MultiChainToken | undefined {
  return tokens.find(t => t.symbol.toUpperCase() === symbol.toUpperCase());
}

/**
 * Helper: Find a chain by ID
 */
export function findChainById(chains: Chain[], chainId: number): Chain | undefined {
  return chains.find(c => c.chainId === chainId);
}

/**
 * Helper: Find a chain by name
 */
export function findChainByName(chains: Chain[], name: string): Chain | undefined {
  return chains.find(c => c.name.toLowerCase() === name.toLowerCase());
}

/**
 * Helper: Get only enabled chains
 */
export function getEnabledChains(chains: Chain[]): Chain[] {
  return chains.filter(c => c.isEnabled);
}
